import { OutputAction, trackOutputAction } from 'analytics';
import { putTextOnClipboard } from 'clipboard';
import Icon, { IconType } from 'components/Icon';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import * as S from './styles';

interface LinkAccessProps {
	publiclyReadable: boolean;
	canChangeAccess: boolean;
	onAccessChange: (next: boolean) => Promise<void>;
	shareUrl: string;
	shareTitle?: string;
	canNativeShare: boolean;
	entityLabel: string;
	trackAction: OutputAction;
}

const LinkAccess = ({
	publiclyReadable,
	canChangeAccess,
	onAccessChange,
	shareUrl,
	shareTitle,
	canNativeShare,
	entityLabel,
	trackAction,
}: LinkAccessProps) => {
	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		void onAccessChange(e.target.value === 'public');
	};

	const onCopyLink = useCallback(async () => {
		if (canNativeShare) {
			try {
				await navigator.share({
					title: shareTitle ?? entityLabel,
					url: shareUrl,
				});
				trackOutputAction(trackAction);
				return;
			} catch (error) {
				if ((error as DOMException)?.name === 'AbortError') return;
				console.warn('Native share failed, falling back to copy', error);
			}
		}
		try {
			await putTextOnClipboard(shareUrl);
			trackOutputAction(trackAction);
			toast.success('Link copied');
		} catch (error) {
			toast.error("Couldn't copy link");
			console.error('Failed to copy link', error);
		}
	}, [canNativeShare, shareUrl, shareTitle, entityLabel, trackAction]);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const linkLabel = canNativeShare ? 'Share link' : 'Copy link';

	return (
		<S.Base>
			<S.Header>Link access</S.Header>
			<S.Row>
				<S.SelectWrapper>
					{canChangeAccess ? (
						<S.Select
							data-testid="share-access-select"
							value={publiclyReadable ? 'public' : 'restricted'}
							onChange={onSelectChange}
						>
							<option value="restricted">Restricted — only editors</option>
							<option value="public">Anyone with the link can view</option>
						</S.Select>
					) : (
						<S.StaticLabel>
							{publiclyReadable ? 'Anyone with the link can view' : 'Restricted — only editors'}
						</S.StaticLabel>
					)}
				</S.SelectWrapper>
				<S.CopyButton data-testid="share-copy-link" onClick={onCopyLink}>
					<Icon
						type={canNativeShare ? IconType.SHARE : IconType.COPY}
						color="#ffffff"
						width={12}
						height={12}
					/>
					{linkLabel}
				</S.CopyButton>
			</S.Row>
		</S.Base>
	);
};

export default LinkAccess;
